define([
	"dojo/_base/array",
	"dojo/_base/declare",
	"dojo/_base/window",
	"dojo/data/util/sorter",
	"underscore",
	"csi/core/extendedPathService",
	"csi/data/AbstractPathBrowserStoreOptions",
	"csi/core/ExtendedPath",
	"csi/reflection"
], function(array, declare, window, sorter, _, {parseExtendedPath, parsePathSteps}, AbstractPathBrowserStoreOptions, ExtendedPath, reflection) {

	return declare(null, {
		//	summary: dojo.data Read/Identity store for browsing object model relationships
		//		the format of each item in the store is as follows:
		// 		{
		//			_store: pointer to this store,
		//			extendedPath: ExtendedPath from the starting reflection class to the reflection class this item belong to,
		//			rcOriginal: the reflection class this item originated from,
		//			rc: the reflection class this item belongs to - it could be base or derived class of rcOriginal,
		//			ra: the reflection attribute this item corresponds to,
		//			isRelationship: true if the item represents the relationship,
		//			localId: the unique id among siblings,
		//			text: the user-friendly name of the item,
		//			value: the value of this item. It could be an arbitrary object.
		//			children: the array of children
		//			_parent: holds the reference to the parent item
		//			_loaded: exactly boolean 'false' if the item is not loaded yet
		// 		}

		//	reflection.Class - the starting reflection class
		_rc: null,

		//	ExtendedPath[]
		_additionalPaths: null,

		// Object[] - items belonging to the starting reflection class
		items: null,

		// AbstractPathBrowserStoreOptions
		options: null,


		// public
		constructor: function(/*reflection.Class*/rc, /*AbstractPathBrowserStoreOptions*/options) {
			this._rc = rc;
			this.options = options ? options : this.newOptionsInstance();
		},

		addTopLevelItemsFromPath: function(/*ExtendedPath*/path) {
			if (this._additionalPaths)
				this._additionalPaths.push(path);
			else
				this._additionalPaths = [path];
		},

		newOptionsInstance: function() {
			// summary: returns the instance of AbstractPathBrowserStoreOptions class or derived from it
			return new AbstractPathBrowserStoreOptions();
		},

		// Read API
		getValue: function(/*item*/item, /*attribute-name-string*/attr, /*value?*/defaultValue) {
			//    summary:
			//        Returns a single attribute value.
			//        Returns defaultValue if and only if *item* does not have a value for *attribute*.
			//        Returns null if and only if null was explicitly set as the attribute value.
			//        Returns undefined if and only if the item does not have a value for the given
			//        attribute (which is the same as saying the item does not have the attribute).
			var v = item ? item[attr] : undefined;
			return (v === undefined) ? defaultValue : v;
		},

		getValues: function(/*item*/item, /*attribute-name-string*/attr) {
			//    summary:
			//        This getValues() method works just like the getValue() method, but getValues()
			//        always returns an array rather than a single attribute value.  The array
			//        may be empty, may contain a single attribute value, or may contain many
			//        attribute values.
			//        If the item does not have a value for the given attribute, then getValues()
			//        will return an empty array: [].  (So, if store.hasAttribute(item, attribute)
			//        returns false, then store.getValues(item, attribute) will return [].)
			var v = item ? item[attr] : null;
			if (v == null)
				return [];
			else if (_.isArray(v))
				return v;
			else
				return [v];
		},

		getAttributes: function(/*item*/item) {
			//    summary:
			//        Returns an array with all the attributes that this item has.  This
			//        method will always return an array; if the item has no attributes
			//        at all, getAttributes() will return an empty array: [].
			var result = [];
			for (var k in item) {
				// ignore all special attributes beginning with the underscore
				if (k.charAt(0) != '_')
					result.push(k);
			}
			return result;
		},

		hasAttribute: function(/*item*/item, /*attribute-name-string*/attr) {
			//    summary:
			//        Returns true if the given *item* has a value for the given *attribute*.
			return item[attr] != null;
		},

		containsValue: function(/*item*/item,
	                            /*attribute-name-string*/attr,
	                            /*anything*/value) {
			//    summary:
			//        Returns true if the given *value* is one of the values that getValues()
			//        would return.
			return this.getValue(item, attr) == value;
		},

		isItem: function(/*anything*/something) {
			//    summary:
			//        Returns true if *something* is an item and came from the store instance.
			//        Returns false if *something* is a literal, an item from another store instance,
			//        or is any object other than an item.
			return something && (something._store == this);
		},

		isItemLoaded: function(/*anything*/something) {
			//    summary:
			//        Returns false if isItem(something) is false.  Returns false if
			//        if isItem(something) is true but the the item is not yet loaded
			//        in local memory (for example, if the item has not yet been read
			//        from the server).
			return this.isItem(something) && something._loaded !== false;
		},

		loadItem: function(/*object*/kwArgs) {
			//    summary:
			//        Given an item, this method loads the item so that a subsequent call
			//        to store.isItemLoaded(item) will return true.  If a call to
			//        isItemLoaded() returns true before loadItem() is even called,
			//        then loadItem() need not do any work at all and will not even invoke
			//        the callback handlers.  So, before invoking this method, check that
			//        the item has not already been loaded.
			//
			//     kwArgs:
			//        An anonymous object that defines the item to load and callbacks to invoke when the
			//        load has completed.  The format of the object is as follows:
			//        {
			//            item: object,
			//            onItem: Function,
			//            onError: Function,
			//            scope: object
			//		  }
			var item = kwArgs.item;

			if (item._loaded !== false)
				return;

			// populate the item and invoke callbacks
			var scope = kwArgs.scope || window.global;
			try {
				this._loadItem(item);

				//Now process all the returned items thro
				if (kwArgs.onItem) {
					kwArgs.onItem.call(scope, item, kwArgs);
				}
			} catch (e) {
				if (kwArgs.onError) {
					kwArgs.onError.call(scope, e, kwArgs);
				} else {
					console.warn(e);
				}
			}
		},

		_loadItem: function(item) {
			//	summary:
			//		populate children

			// children do exist for the relationship only
			if (item.isRelationship) {
				// add the relationship Id to the path and calculate the reflection class from it
				var id = item.value;
				var rc = item.rc;
				var path = item.extendedPath;

				var relation = rc.getRelation(id, true, true);
				if (relation) {
					var rcFromPath = relation.getDestinationClass(rc);
					if (rcFromPath) {
						path = path.clone();
						path.addRelationStep(id, rcFromPath.getType());
						var children = this._getItemsFromRC(path, rcFromPath);
						array.forEach(children, function(child) {
							child._parent = item;
						});
						item.children = children;
					} else {
						console.error("Can not find the destination class for the relation with Id=", id, "from Reflection Class=", rc);
					}
				} else {
					console.error("Can not find the relation with Id=", id, "for Reflection Class=", rc);
				}
			}

			delete item._loaded;
		},

		getFeatures: function() {
			//    summary:
			//        The getFeatures() method returns an simple keyword values object
			//        that specifies what interface features the datastore implements.
			return {"dojo.data.api.Read": true, "dojo.data.api.Identity": true};
		},

		close: function(/*dojo.data.api.Request || kwArgs || null*/request) {
			//    summary:
			//        The close() method is intended for instructing the store to 'close' out
			//        any information associated with a particular request.
		},

		getLabel: function(/*item*/item) {
			//    summary:
			//        Method to inspect the item and return a user-readable 'label' for the item
			//        that provides a general/adequate description of what the item is.
			return item.text;
		},

		getLabelAttributes: function(/*item*/item) {
			//    summary:
			//        Method to inspect the item and return an array of what attributes of the item were used
			//        to generate its label, if any.
			return ["text"];
		},

		fetch: function(request) {
			//	summary:
			//		Fetch  items that match to a query
			//	request:
			//		A request object
			//	fetchHandler:
			//		A function to call for fetched items
			//	errorHandler:
			//		A function to call on error

			var items = null;
			var query = request.query;
			if (query == "topLevel") {
				items = this._getTopLevelItems();
			} else {
				items = this._findByQuery(query);
			}

			if (items)
				this._processResult(items, request);
			else {
				if (request.onError) {
					var scope = request.scope || window.global;
					request.onError.call(scope, new Error("Unknown query:" + request.query), request);
				}
			}

			return {abort: function() { }};
		},

		_processResult: function(items, request) {
			var scope = request.scope || window.global;
			try {
				//Invoke the onBegin handler, if any, to return the
				//size of the dataset as indicated by the service.
				if (request.onBegin) {
					request.onBegin.call(scope, -1, request);
				}

				//Now process all the returned items thro
				if (request.onItem) {
					var i;
					for (i = 0; i < items.length; i++) {
						request.onItem.call(scope, items[i], request);
					}
					items = null;
				}
				if (request.onComplete) {
					request.onComplete.call(scope, items, request);
				}
			} catch (e) {
				if (request.onError) {
					request.onError.call(scope, e, request);
				} else {
					console.warn(e);
				}
			}
		},

		_getTopLevelItems: function() {
			if (!this.items) {
				this.items = this._getItemsFromRC(new ExtendedPath(this._rc.getType()), this._rc);
				if (this._additionalPaths) {
					array.forEach(this._additionalPaths, function(path) {
						var rc = reflection.getClass(path.getEndType());
						var items = this._getItemsFromRC(path, rc);
						this.items = this.items.concat(items);
					}, this);
					this._sortItems(this.items);
				}
			}
			return this.items;
		},

		_isSupportedQuery: function(/*Object*/query) {
			// summary: returns true if the query is supported
			//		supported queries are:
			//			1. {children:{path: ..., localId: ...}}
			//			2. {path: ..., localId: ...}
			//			3. {text: ..., depth: number, }
			if (query.children != null && query.path == null && query.localId == null)
				return true;

			if (query.children == null && query.localId != null)
				return true;

			if (query.text != null)
				return true;

			return false;
		},

		_walkItems: function(/*Item[]*/items,/*Function*/visitorFunc) {
			if (!items)
				return;

			for (var i = 0; i < items.length; ++i) {
				var item = items[i];

				if (!this.isItemLoaded(item))
					this.loadItem({item: item});

				if (visitorFunc(item))
					return;
			}
		},

		_findByQuery: function(query) {
			//	summary: find the item by query
			//		supported queries are:
			//			1. {children:{path: ..., localId: ...}}
			//			2. {path: ..., localId: ...}
			//			3. {text: ..., depth: number}
			var self = this;

			if (!this._isSupportedQuery(query)) {
				console.error("AbstractPathBrowserStore._findByQuery does not support the query=", query);
				return null;
			}

			// parent item, item found and the array of items the found item belong to
			var parentItem = null;
			var items = this._getTopLevelItems();
			var item;

			function findByAttributeValue(items, attr, value) {
				var foundItem;
				self._walkItems(items, function(item) {
					if (value == self.getValue(item, attr)) {
						foundItem = item;
						return true;
					}
				});
				return foundItem;
			}

			function findItemWithLocalId(items, value) {
				return findByAttributeValue(items, "localId", value);
			}

			function findRecursively(items, attr, regexp, curLevel, maxDepth, results) {
				var newResults = [];
				var relationships = [];
				self._walkItems(items, function(item) {
					if (self.getValue(item, "isRelationship"))
						relationships.push(item);
					else if (regexp.test(self.getValue(item, attr)))
						newResults.push(item);
				});

				Array.prototype.push.apply(results, newResults);

				if (++curLevel <= maxDepth) {
					array.forEach(relationships, function(relationshipItem) {
						var children = self.getValues(relationshipItem, "children");
						findRecursively(children, attr, regexp, curLevel, maxDepth, results);
					});
				}
			}

			// what kind of query is it?
			if (query.text != null) {
				// search query by text
				var results = [];
				// replace '*' wildcard with regexp-friendly '.*'
				var queryText = query.text.replace(/\*/g, ".*");

				// do not match an empty string, otherwise find items recursively up to the given depth
				if (queryText)
					findRecursively(items, "text", new RegExp(queryText, "mi"), 0, query.depth || 0, results);

				// sort results first by the depth, second by the path
				results.sort(function(item1, item2) {
					var result = item1.extendedPath.depth - item2.extendedPath.depth;
					if (result == 0) {
						var path1 = item1.extendedPath.getStandardPath();
						var path2 = item2.extendedPath.getStandardPath();
						if (path1 == null && path2 == null)
							result = 0;
						else if (path1 == null && path2 != null)
							result = -1;
						else if (path1 != null && path2 == null)
							result = 1;
						else
							result = path1.localeCompare(path2);
					}
					return result;
				});
				return results;
			} else {
				// query with a localId
				var objToFind = query.children || query;

				// go straight to the correct item in case of the path or extendedPath is present
				var steps = null;

				if (objToFind.path) {
					steps = array.map(parsePathSteps(objToFind.path), function(step) {
						return step.a;
					});
				} else if (objToFind.extendedPath) {
					// first, try to understand if we have some items with the same extended path
					var possibleItems = array.filter(items, function(item) {return objToFind.extendedPath.equals(item.extendedPath);});

					// if we have these items, search among them, otherwise go to children
					if (possibleItems.length > 0)
						items = possibleItems;
					else
						steps = objToFind.extendedPath.relationSteps;
				}

				if (steps) {
					array.forEach(steps, function(step) {
						if (parentItem = findItemWithLocalId(items, this.getRelationshipLocalId(step))) {
							items = this.getValues(parentItem, "children");
						}
					}, this);
				}

				// now verify that the child object is contained in items
				item = findItemWithLocalId(items, objToFind.localId);

				if (query.children)
					return (item && parentItem) ? [parentItem] : [];
				else
					return item ? [item] : [];
			}
		},

		// Identity API
		getIdentity: function(/*item*/item) {
			//    summary:
			//        Returns a unique identifier for an item.  The return value will be
			//        either a string or something that has a toString() method.
			if (item.extendedPath)
				return item.extendedPath.toString() + '|' + item.localId;
			else
				return item.localId;
		},

		getIdentityAttributes: function(/*item*/item) {
			//    summary:
			//        Returns an array of attribute names that are used to generate the identity.
			//        For most stores, this is a single attribute, but for some complex stores
			//        such as RDB backed stores that use compound (multi-attribute) identifiers
			//        it can be more than one.  If the identity is not composed of attributes
			//        on the item, it will return null.  This function is intended to identify
			//        the attributes that comprise the identity so that so that during a render
			//        of all attributes, the UI can hide the the identity information if it
			//        chooses.
			return ["extendedPath", "localId"];
		},

		fetchItemByIdentity: function(/*object*/kwArgs) {
			//    summary:
			//        Given the identity of an item, this method returns the item that has
			//        that identity through the onItem callback.  Conforming implementations
			//        should return null if there is no item with the given identity.
			//        Implementations of fetchItemByIdentity() may sometimes return an item
			//        from a local cache and may sometimes fetch an item from a remote server,
			//
			//    kwArgs:
			//        An anonymous object that defines the item to locate and callbacks to invoke when the
			//        item has been located and load has completed.  The format of the object is as follows:
			//        {
			//            identity: string|object,
			//            onItem: Function,
			//            onError: Function,
			//            scope: object
			//        }
			var scope = kwArgs.scope || window.global;
			var pathAndId = kwArgs.identity.split('|');
			var path = parseExtendedPath(pathAndId[0]);
			var id = pathAndId[1];

			var request = {
				scope: kwArgs.scope,
				query: {
					extendedPath: path,
					localId: id
				},
				onError: kwArgs.onError,
				onComplete: function(items) {
					var item = items[0];
					kwArgs.onItem.call(scope, item);
				}
			};
			return this.fetch(request);
		},

		_getItemsFromRC: function(/*ExtendedPath*/path,/*reflection.Class*/rcFromPath) {
			// retrieve items and sort them

			var items = this.getItemsFromRC(path, rcFromPath);

			this._sortItems(items);

			return items;
		},

		_sortItems: function(/*Object[]*/items) {
			items.sort(sorter.createSortFunction(this.options.sortSpec, this));
		},

		getItemsFromRC: function(/*ExtendedPath*/extendedPath,/*reflection.Class*/rcFromPath) {
			// fill out outgoing relationships and mark them as not loaded yet
			var items = [];
			var relations = rcFromPath.getRelations(true, true);

			array.forEach(relations, function(ra) {
				var id = ra.getId();

				var item = {
					_store: this,
					extendedPath,
					ra,
					rc: rcFromPath,
					rcOriginal: rcFromPath,
					isRelationship: true,
					localId: this.getRelationshipLocalId(id),
					text: id,
					value: id,
					_loaded: false
				};
				items.push(item);
			}, this);

			return items;
		},

		getRelationshipLocalId: function(id) {
			return id + '$';
		}
	});
});